/**
 * React Query hook around `fetchInterviewersList`.
 *
 * Why a hook (and not the Select's built-in async cache):
 *   - Shared cache across every re-mount of the interviewer dropdown
 *     (the legacy async-select cache was local to the instance, so
 *     closing + re-opening the menu re-fetched identical data).
 *   - Per-visible-week keying so switching calendar weeks warms a
 *     separate cache bucket — navigating back instantly rehydrates.
 *   - `placeholderData` keeps the previous week's list on screen while
 *     the new one fetches; avoids a "Loading…" flash on week change.
 *   - `queryClient.fetchQuery` inside the Select loader dedupes
 *     simultaneous requests from different consumers of the same key.
 */
import {
  useQuery,
  keepPreviousData,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  fetchInterviewersList,
  type FetchInterviewersListArgs,
  type InterviewersListResult,
} from './fetchInterviewersList';

export interface UseInterviewersListArgs {
  /** Free-text search (trimmed and lowercased before becoming part of the key). */
  query: string;
  /** Sunday of the visible calendar week (YYYY-MM-DD). */
  weekStartDate: string;
  /** Omit for adhoc flows — backend uses it to surface pool interviewers first. */
  atsJobStageActionId?: number;
  enabled?: boolean;
}

/** Stable key shape used by the hook and the imperative `prefetch` / `fetchQuery` helpers. */
export function interviewersListQueryKey(args: {
  query: string;
  weekStartDate: string;
  atsJobStageActionId?: number;
}): readonly unknown[] {
  return [
    'ats',
    'interviewers_list',
    args.weekStartDate,
    args.atsJobStageActionId ?? null,
    args.query.trim().toLowerCase(),
  ];
}

function buildFetcher(
  args: Omit<UseInterviewersListArgs, 'enabled'>
): (_ctx: { signal?: AbortSignal }) => Promise<InterviewersListResult> {
  const fetchArgs: FetchInterviewersListArgs = {
    query: args.query,
    weekStartDate: args.weekStartDate,
    ...(typeof args.atsJobStageActionId === 'number' &&
    args.atsJobStageActionId > 0
      ? { atsJobStageActionId: args.atsJobStageActionId }
      : {}),
  };
  return ({ signal }) => fetchInterviewersList({ ...fetchArgs, signal });
}

export function useInterviewersList(
  args: UseInterviewersListArgs
): UseQueryResult<InterviewersListResult, Error> {
  const { enabled = true, ...rest } = args;
  return useQuery<InterviewersListResult, Error>({
    queryKey: interviewersListQueryKey(rest),
    queryFn: buildFetcher(rest),
    enabled: enabled && !!rest.weekStartDate,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/**
 * Imperative fetch used by the Select's `loadOptions`. Goes through
 * React Query so concurrent openers share a single in-flight request
 * and already-cached pages return synchronously.
 */
export function fetchInterviewersListViaCache(
  queryClient: QueryClient,
  args: Omit<UseInterviewersListArgs, 'enabled'>
): Promise<InterviewersListResult> {
  return queryClient.fetchQuery<InterviewersListResult>({
    queryKey: interviewersListQueryKey(args),
    queryFn: buildFetcher(args),
    staleTime: 30_000,
  });
}

/**
 * Fire-and-forget prefetch — call from a `useEffect` when the visible
 * calendar week changes so the user never waits on the very first
 * dropdown open for that week.
 */
export function prefetchInterviewersList(
  queryClient: QueryClient,
  args: Omit<UseInterviewersListArgs, 'enabled'>
): void {
  void queryClient.prefetchQuery<InterviewersListResult>({
    queryKey: interviewersListQueryKey(args),
    queryFn: buildFetcher(args),
    staleTime: 30_000,
  });
}
